import React, { useEffect, useMemo, useState } from 'react';
import { getApiBaseUrl } from '../config/apiBase';
import InspectDrawerShell from './InspectDrawerShell';
import {
  collectConfirmedHibpBreaches,
  hibpBreachDisplayName,
  resolveHibpBreachCount,
} from '../utils/hibpBreachCount';
import { sanitizeHtmlToText } from '../utils/sanitizeHtml';

const EMPTY_STREAM_BREACHES = Object.freeze([]);

function hibpInspectPayloadFromStatus(status) {
  if (!status || typeof status !== 'object') return null;
  const details = status.details && typeof status.details === 'object' ? status.details : {};
  const nested =
    details.hibp_inspect && typeof details.hibp_inspect === 'object' ? details.hibp_inspect : null;
  if (nested) return nested;
  if (details.breaches || status.breaches_count != null || status.api_error) {
    return {
      email: status.email || status.target,
      breaches_count: status.breaches_count ?? 0,
      breaches: details.breaches || [],
      api_error: status.api_error,
      account_url: status.inspect_url || status.url,
      status: status.status,
    };
  }
  return null;
}

function normalizeInspectBreach(breach) {
  if (!breach || typeof breach !== 'object') return null;
  const title =
    breach.title ||
    breach.breach_name ||
    breach.name ||
    breach.details?.title ||
    breach.details?.name ||
    '';
  if (!title) return null;
  return {
    title,
    breach_name: title,
    name: breach.name || title,
    breach_date: breach.breach_date || breach.date || breach.details?.date || '',
    date: breach.breach_date || breach.date || breach.details?.date || '',
    data_classes: breach.data_classes || breach.dataclasses || breach.details?.dataclasses || [],
    dataclasses: breach.data_classes || breach.dataclasses || breach.details?.dataclasses || [],
    description: breach.description || breach.details?.description || '',
    breach_url: breach.breach_url || breach.url || '',
    url: breach.breach_url || breach.url || '',
  };
}

function mergeInspectBreaches(statusPayload, streamBreaches, status) {
  const fromStatus = Array.isArray(statusPayload?.breaches) ? statusPayload.breaches : [];
  const fromStream = collectConfirmedHibpBreaches([], {
    status,
    breaches: Array.isArray(streamBreaches) ? streamBreaches : [],
  });
  const merged = [];
  const seen = new Set();
  for (const raw of [...fromStatus, ...fromStream]) {
    const row = normalizeInspectBreach(raw) || normalizeInspectBreach({
      breach_name: hibpBreachDisplayName(raw),
      ...raw,
    });
    if (!row) continue;
    const key = String(row.title || '').toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    merged.push(row);
  }
  return merged;
}

function breachTitle(breach) {
  return (
    breach?.title ||
    breach?.breach_name ||
    breach?.name ||
    'Unknown breach'
  );
}

/**
 * HIBP breach inspector — right slide-over drawer (Email Search).
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: Function,
 *   email?: string,
 *   status?: object,
 *   streamBreaches?: object[],
 * }} props
 */
export default function HibpInspectModal({
  isOpen,
  onClose,
  email,
  status,
  streamBreaches,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const streamList = Array.isArray(streamBreaches) ? streamBreaches : EMPTY_STREAM_BREACHES;

  const cached = useMemo(() => hibpInspectPayloadFromStatus(status), [status]);
  const targetEmail = email || cached?.email || status?.email || status?.target || '';
  const expectedCount = resolveHibpBreachCount([], {
    status,
    breaches: streamList,
  });
  const mergedCachedBreaches = useMemo(
    () => mergeInspectBreaches(cached, streamList, status),
    [cached, streamList, status],
  );
  const cachedComplete =
    Boolean(cached) &&
    !cached?.api_error &&
    mergedCachedBreaches.length > 0 &&
    mergedCachedBreaches.length >= Math.max(expectedCount, 1);

  useEffect(() => {
    if (!isOpen) {
      setLoading(false);
      setError('');
      setPayload(null);
      return undefined;
    }

    if (cachedComplete) {
      setPayload({
        ...(cached || {}),
        breaches_count: Math.max(expectedCount, mergedCachedBreaches.length),
        breaches: mergedCachedBreaches,
      });
      setError('');
      setLoading(false);
      return undefined;
    }

    if (!targetEmail || !targetEmail.includes('@')) {
      setPayload(
        cached
          ? {
              ...cached,
              breaches: mergedCachedBreaches,
              breaches_count: Math.max(
                Number(cached.breaches_count) || 0,
                mergedCachedBreaches.length,
                expectedCount,
              ),
            }
          : null,
      );
      setError('No valid email for HIBP lookup.');
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const base = getApiBaseUrl();
        const url = `${base}/hibp-email-breaches?email=${encodeURIComponent(targetEmail)}`;
        const res = await fetch(url);
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          throw new Error(data?.error || data?.api_error || `HTTP ${res.status}`);
        }
        const apiBreaches = mergeInspectBreaches(data, streamList, status);
        setPayload({
          ...data,
          breaches: apiBreaches.length > 0 ? apiBreaches : mergeInspectBreaches(cached, streamList, status),
          breaches_count: Math.max(
            Number(data?.breaches_count) || 0,
            apiBreaches.length,
            expectedCount,
          ),
        });
        if (data?.api_error) {
          setError(String(data.api_error));
        }
      } catch (err) {
        if (cancelled) return;
        setPayload({
          ...(cached || {}),
          breaches: mergedCachedBreaches,
          breaches_count: Math.max(
            Number(cached?.breaches_count) || 0,
            mergedCachedBreaches.length,
            expectedCount,
          ),
        });
        setError(err?.message || 'HIBP lookup failed.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    cached,
    cachedComplete,
    targetEmail,
    expectedCount,
    mergedCachedBreaches,
    streamList,
    status,
  ]);

  if (!isOpen) return null;

  const data = payload || {
    ...(cached || {}),
    breaches: mergedCachedBreaches,
    breaches_count: Math.max(
      Number(cached?.breaches_count) || 0,
      mergedCachedBreaches.length,
      expectedCount,
    ),
  };
  const breaches = Array.isArray(data?.breaches) ? data.breaches : [];
  const count = Math.max(
    Number(data?.breaches_count) || 0,
    breaches.length,
    expectedCount,
  );
  const accountUrl = data?.account_url || status?.inspect_url || status?.url;
  const unavailable = Boolean(data?.api_error || error);
  const clean = !unavailable && count === 0;

  return (
    <InspectDrawerShell
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel="HIBP breach inspection"
      testId="hibp-inspect-modal"
      eyebrow="Have I Been Pwned"
      title="Breach Intelligence"
      sourceHref={accountUrl}
      subtitle={
        targetEmail ? (
          <p className="mt-1 font-mono text-[11px] text-slate-400">{targetEmail}</p>
        ) : null
      }
    >
      {loading ? (
        <p className="text-sm text-slate-400" data-testid="hibp-inspect-loading">
          Querying HIBP API…
        </p>
      ) : null}

      {unavailable ? (
        <div
          className="rounded-xl border border-amber-500/30 bg-amber-950/30 p-3 text-sm text-amber-100"
          data-testid="hibp-inspect-unavailable"
        >
          {data?.api_error || error || 'HIBP API unavailable.'}
        </div>
      ) : null}

      {!loading && !unavailable ? (
        <div
          className={`rounded-xl border p-3 ${
            clean
              ? 'border-emerald-500/30 bg-emerald-950/30 text-emerald-100'
              : 'border-red-500/30 bg-red-950/30 text-red-100'
          }`}
          data-testid="hibp-inspect-summary"
        >
          {clean
            ? 'No known breach exposure for this mailbox.'
            : `Exposed in ${count} known breach record(s).`}
        </div>
      ) : null}

      {!loading && breaches.length > 0 ? (
        <ul className="m-0 list-none space-y-3 p-0" data-testid="hibp-inspect-breach-list">
          {breaches.map((breach) => {
            const title = breachTitle(breach);
            const key = `${title}-${breach.breach_date || breach.date || ''}`;
            const classes = breach.data_classes || breach.dataclasses || [];
            const breachUrl = breach.breach_url || breach.url;
            return (
              <li
                key={key}
                className="rounded-xl border border-white/10 bg-slate-900/60 p-4"
                data-testid="hibp-inspect-breach-item"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h4 className="text-sm font-bold text-slate-100">{title}</h4>
                    {breach.breach_date || breach.date ? (
                      <p className="mt-0.5 text-[10px] font-bold uppercase tracking-tight text-slate-500">
                        Breach date: {breach.breach_date || breach.date}
                      </p>
                    ) : null}
                  </div>
                  {breachUrl ? (
                    <a
                      href={breachUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[10px] font-bold uppercase tracking-widest text-cyan-300 hover:underline"
                    >
                      View on HIBP
                    </a>
                  ) : null}
                </div>
                {Array.isArray(classes) && classes.length > 0 ? (
                  <ul className="mt-2 flex list-none flex-wrap gap-1.5 p-0">
                    {classes.map((dc) => (
                      <li
                        key={`${key}-${dc}`}
                        className="feed-keyword-tag border-red-500/30 text-red-200"
                      >
                        {dc}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {breach.description ? (
                  <p className="mt-2 text-xs text-slate-400">
                    {sanitizeHtmlToText(breach.description)}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {!loading && !unavailable && count > 0 && breaches.length < count ? (
        <p className="text-xs text-amber-200" data-testid="hibp-inspect-partial">
          Showing {breaches.length} of {count} breach record(s). Retry Inspect if the list is incomplete.
        </p>
      ) : null}

      {accountUrl ? (
        <p className="break-all font-mono text-[10px] text-slate-500">{accountUrl}</p>
      ) : null}
    </InspectDrawerShell>
  );
}
